<template>
  <ModalStructure
    :class="$style.modal"
    :hasBlur="false"
    :shouldCloseOnBlur="false"
    :showModal="showOptionsModal"
    showCloseButton
    @close="handleClose"
  >
    <template v-slot:header>
      <HeadingLarge :class="$style.header">
        <ApplicationAvatar />
        <div :class="$style.header__title">
          Import New and Update Existing Users from {{ displayName }}
          <p
            :class="$style.header__count"
          >
            Total Users: {{ countUsers > 0 ? countUsers : 'Not available' }}
          </p>
        </div>
      </HeadingLarge>
    </template>
    <template v-slot:body>
      <div v-if="customApiFeatureFlag">
        <ImportUsersPaywallMessage
          v-if="isFreeUserOverLimit"
          :class="$style.paywall"
          :userLimitMessage="maxFreeUsersLimitMessage"
        />
        <UserCapBanner
          v-else-if="isProviderOrg"
          altLicenseText
          :class="$style.userCapBanner"
          :totalCountOfAllItems="countUsers"
        />
      </div>
      <RadioGroup
        v-model="importOption"
        :class="$style.radioGroup"
        :inline="false"
        :isHelperTextMarkdown="false"
        :isMarkdown="false"
        label="Select an import option:"
        name="radiogroup"
        :options="importOptions"
        :required="false"
        showValidationMessage
        value="1"
      />
    </template>
    <template v-slot:footer>
      <div :class="$style.footerContainer">
        <ButtonGroupSave
          :class="$style.btnGroup"
          :isFullWidth="false"
          :primaryButtonProps="primaryButtonProps"
          :secondaryButtonProps="secondaryButtonProps"
          @primaryAction="continueImport"
          @secondaryAction="handleClose"
        />
      </div>
    </template>
  </ModalStructure>
</template>

<script>
import {
  ButtonGroupSave,
  HeadingLarge,
  ModalStructure,
  RadioGroup,
  Types,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapState } from 'vuex';
import ApplicationAvatar from 'client/applications/ApplicationAvatar.vue';
import ImportUsersPaywallMessage from 'client/components/ImportUsers/ImportUsersPaywallMessage';
import UserCapBanner from 'client/Payments/components/UserCapBanner';

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const { PrimaryButtonProps, SecondaryButtonProps } = Types;

export default {
  name: 'IdmUsersImportOptions',

  components: {
    ApplicationAvatar,
    ButtonGroupSave,
    HeadingLarge,
    ImportUsersPaywallMessage,
    ModalStructure,
    RadioGroup,
    UserCapBanner,
  },

  props: {
    countUsers: {
      type: Number,
      default: 0,
    },
    displayName: {
      type: String,
      required: true,
    },
    showOptionsModal: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    importOptions: [
      { label: 'Import new users and user updates', value: 1 },
      { label: 'Only import new users', value: 2 },
      { label: 'Only import user updates', value: 3 },
      { label: 'View and select specific new users to import (updates not supported)', value: 4 },
    ],
    importOption: 1,
  }),

  computed: {
    ...mapState(settingsModule, [
      'isCustomer',
      'maxSystemUsers',
      'isProviderOrg',
    ]),

    customApiFeatureFlag() {
      return this.$hasFeatureFlag(this.$featureFlags.importUserUpdatesCustomApi);
    },

    isFreeUserOverLimit() {
      return !this.isCustomer && this.countUsers > this.maxSystemUsers;
    },

    maxFreeUsersLimitMessage() {
      return `Importing users may put you over the ${this.maxSystemUsers} user limit on your org and cause the import to fail.
To grow your account beyond ${this.maxSystemUsers} users, select one of our plans.`;
    },
    primaryButtonProps() {
      return PrimaryButtonProps({ text: 'Continue' });
    },

    secondaryButtonProps() {
      return SecondaryButtonProps({ text: 'Cancel' });
    },
  },

  methods: {
    continueImport() {
      if (this.importOption === 4) {
        this.$emit('selectUsersImport');
      } else {
        const importCase = {
          1: ['users.create', 'users.update'],
          2: ['users.create'],
          3: ['users.update'],
        };
        this.$emit('importUsers', importCase[this.importOption]);
      }
    },

    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<style module scoped>
.modal {
  --modal-width: 120ch;
}

.header {
  border-bottom: 1px solid #e3e8e9;
  display: flex;
  padding-bottom: var(--jc-spacer-small);
}

.header__title {
  margin-left: var(--jc-spacer);
}

.header__count {
  font-size: var(--count-label-font-size, var(--jcBody));
  font-weight: var(--count-label-font-weight, 600);
  margin-top: 2px;
}

.paywall {
  background-color: var(--jcInformational50);
  white-space: pre-wrap;
}

.radioGroup {
  padding-left: 19ch;
  padding-top: var(--jc-spacer);
}

.radioGroup >>> > span {
  font-size: 18px;
  padding-bottom: var(--jc-spacer);
}

.userCapBanner {
  background-color: var(--jcInformational100);
}

.footerContainer {
  width: 100%;
}
</style>
